import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Activity,
  Database,
  Server,
  CreditCard,
  Mail,
  Shield,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Users,
  ShoppingBag,
  Store,
  Zap,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  const configs = {
    healthy: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: 'Opérationnel' },
    connected: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: 'Connecté' },
    configured: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: 'Configuré' },
    operational: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: 'Opérationnel' },
    warning: { color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle, label: 'Attention' },
    degraded: { color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle, label: 'Dégradé' },
    not_configured: { color: 'bg-gray-100 text-gray-700', icon: XCircle, label: 'Non configuré' },
    error: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Erreur' },
    critical: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Critique' },
  };
  
  const config = configs[status] || configs.error;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

const ServiceCard = ({ title, icon: Icon, status, details, color }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-xl ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{title}</h3>
            <StatusBadge status={status} />
          </div>
        </div>
      </div>
      {details && (
        <div className="mt-4 text-sm text-gray-600 space-y-1">
          {Object.entries(details).map(([key, value]) => (
            <div key={key} className="flex justify-between">
              <span className="capitalize">{key.replace(/_/g, ' ')}:</span>
              <span className="font-medium">{String(value)}</span>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

export default function AdminHealth() {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchHealthData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/health/dashboard`);
      setHealthData(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching health data:', error);
      toast.error('Erreur lors du chargement des données de santé');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealthData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealthData, 30000);
    return () => clearInterval(interval);
  }, [fetchHealthData]);

  const getOverallStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'bg-emerald-500';
      case 'warning': return 'bg-yellow-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <AdminLayout title="Santé du Système">
      {/* Overall Status Banner */}
      {healthData && (
        <div className={`${getOverallStatusColor(healthData.overall_status)} rounded-2xl p-6 mb-8 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Activity className="w-10 h-10" />
              <div>
                <h2 className="text-2xl font-bold capitalize">
                  Système {healthData.overall_status === 'healthy' ? 'Opérationnel' : 
                           healthData.overall_status === 'warning' ? 'Attention Requise' : 'Critique'}
                </h2>
                <p className="text-white/80">
                  Dernière vérification: {lastRefresh?.toLocaleTimeString('fr-FR')}
                </p>
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={fetchHealthData}
              disabled={loading}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </div>
      )}

      {loading && !healthData ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
        </div>
      ) : healthData ? (
        <>
          {/* System Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ShoppingBag className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Commandes aujourd'hui</p>
                    <p className="text-2xl font-bold">{healthData.metrics?.orders_today || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Users className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Nouveaux utilisateurs</p>
                    <p className="text-2xl font-bold">{healthData.metrics?.new_users_today || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Store className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total restaurants</p>
                    <p className="text-2xl font-bold">{healthData.metrics?.total_restaurants || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total commandes</p>
                    <p className="text-2xl font-bold">{healthData.metrics?.total_orders || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Services Status */}
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Server className="w-5 h-5" />
            État des Services
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <ServiceCard
              title="Base de données"
              icon={Database}
              status={healthData.services?.database?.status}
              details={{
                'Latence': `${healthData.services?.database?.latency_ms || '-'} ms`,
                'Collections': healthData.services?.database?.collections || '-',
              }}
              color="bg-blue-100 text-blue-600"
            />
            
            <ServiceCard
              title="API Backend"
              icon={Server}
              status={healthData.services?.api?.status}
              details={{
                'Uptime': healthData.services?.api?.uptime || '-',
                'Temps réponse': `${healthData.services?.api?.avg_response_time_ms || '-'} ms`,
              }}
              color="bg-purple-100 text-purple-600"
            />
            
            <ServiceCard
              title="Stripe (Paiements)"
              icon={CreditCard}
              status={healthData.services?.stripe?.status}
              details={{
                'Mode': healthData.services?.stripe?.mode || '-',
              }}
              color="bg-indigo-100 text-indigo-600"
            />
            
            <ServiceCard
              title="Email (Resend)"
              icon={Mail}
              status={healthData.services?.email?.status}
              details={{
                'Provider': healthData.services?.email?.provider || '-',
              }}
              color="bg-pink-100 text-pink-600"
            />
            
            <ServiceCard
              title="Sentry (Monitoring)"
              icon={Shield}
              status={healthData.services?.sentry?.status}
              details={{
                'Environnement': healthData.services?.sentry?.environment || '-',
              }}
              color="bg-orange-100 text-orange-600"
            />
            
            <ServiceCard
              title="Performance"
              icon={Zap}
              status={healthData.errors?.error_rate === 'low' ? 'healthy' : 
                      healthData.errors?.error_rate === 'medium' ? 'warning' : 'critical'}
              details={{
                'Erreurs/heure': healthData.errors?.errors_last_hour || 0,
                'Erreurs/24h': healthData.errors?.errors_last_24h || 0,
              }}
              color="bg-yellow-100 text-yellow-600"
            />
          </div>

          {/* Recent Errors */}
          {healthData.recent_errors && healthData.recent_errors.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Erreurs Récentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {healthData.recent_errors.map((error, index) => (
                    <div key={index} className="p-3 bg-red-50 rounded-lg border border-red-100">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-red-800">{error.type}</p>
                          <p className="text-sm text-red-600">{error.message}</p>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-red-500">
                          <Clock className="w-3 h-3" />
                          {new Date(error.timestamp).toLocaleString('fr-FR')}
                        </div>
                      </div>
                      {error.url && (
                        <p className="text-xs text-red-400 mt-1">URL: {error.url}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Configuration Checklist */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Checklist de Configuration Production</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { name: 'Base de données MongoDB', configured: healthData.services?.database?.status === 'connected' },
                  { name: 'Clé Stripe (Paiements)', configured: healthData.services?.stripe?.status === 'configured' },
                  { name: 'Clé Resend (Emails)', configured: healthData.services?.email?.status === 'configured' },
                  { name: 'Sentry (Monitoring erreurs)', configured: healthData.services?.sentry?.status === 'configured' },
                  { name: 'HTTPS activé', configured: window.location.protocol === 'https:' },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    {item.configured ? (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <span className={item.configured ? 'text-gray-700' : 'text-red-600 font-medium'}>
                      {item.name}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-center py-12 text-gray-500">
          Impossible de charger les données de santé
        </div>
      )}
    </AdminLayout>
  );
}
